/**
 * Exact rational arithmetic on BigInt.
 *
 * The balancer solves a linear system, and floating point would turn a
 * coefficient of 3 into 2.9999999999999996 and a genuine zero into 1e-17 —
 * which is the difference between "balanced" and "no solution". Nothing here
 * ever touches a float.
 */

export interface Rational {
  readonly n: bigint;
  readonly d: bigint;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x;
}

export function rational(n: bigint | number, d: bigint | number = 1n): Rational {
  let num = BigInt(n);
  let den = BigInt(d);
  if (den === 0n) throw new Error('A rational cannot have a denominator of zero.');
  if (den < 0n) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den) || 1n;
  return { n: num / g, d: den / g };
}

export const ZERO = rational(0n);
export const ONE = rational(1n);

export const add = (a: Rational, b: Rational): Rational => rational(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Rational, b: Rational): Rational => rational(a.n * b.d - b.n * a.d, a.d * b.d);
export const mul = (a: Rational, b: Rational): Rational => rational(a.n * b.n, a.d * b.d);

export function div(a: Rational, b: Rational): Rational {
  if (b.n === 0n) throw new Error('Division by zero in the balancer.');
  return rational(a.n * b.d, a.d * b.n);
}

export const neg = (a: Rational): Rational => rational(-a.n, a.d);
export const isZero = (a: Rational): boolean => a.n === 0n;
export const isNegative = (a: Rational): boolean => a.n < 0n;
export const equals = (a: Rational, b: Rational): boolean => a.n === b.n && a.d === b.d;

export function toString(a: Rational): string {
  return a.d === 1n ? String(a.n) : `${a.n}/${a.d}`;
}

function lcm(a: bigint, b: bigint): bigint {
  if (a === 0n || b === 0n) return 0n;
  const g = gcd(a, b);
  const result = (a / g) * b;
  return result < 0n ? -result : result;
}

/**
 * Scales a vector of rationals to the smallest whole numbers that keep the
 * same ratios — which is exactly what a balanced equation needs.
 */
export function toSmallestIntegers(values: Rational[]): bigint[] {
  if (values.length === 0) return [];

  let denominators = 1n;
  for (const value of values) denominators = lcm(denominators, value.d) || 1n;

  const scaled = values.map((value) => (value.n * denominators) / value.d);

  let common = 0n;
  for (const value of scaled) common = gcd(common, value);
  if (common === 0n) return scaled;

  return scaled.map((value) => value / common);
}
